import { useMemo } from 'react';

import { createTheme, PaletteOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { ThemeModeProvider, useThemeMode } from './ThemeContext';

/**
 * Clinical Intelligence Theme
 *
 * Aligned with the Cortex design system:
 * - Clinical white base (90%) / Dark terminal base in dark mode
 * - BID brand colors strategic (10%)
 * - Data-dense, precise, institutional
 */

const clinicalBase: PaletteOptions = {
  primary: {
    main: '#FF5650',
    light: '#E64D47',
    dark: '#CC453F',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#57606A',
    light: '#6E7781',
    dark: '#24292F',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#FF5650',
    light: '#FFE8E7',
    dark: '#CC453F',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#BF8700',
    light: '#FFF8E0',
    dark: '#955F00',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0969DA',
    light: '#DDF4FF',
    dark: '#0550AE',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2DA44E',
    light: '#DFF7E8',
    dark: '#1A7F37',
    contrastText: '#FFFFFF',
  },
};

const lightPalette: PaletteOptions = {
  ...clinicalBase,
  mode: 'light',
  text: {
    primary: '#24292F',
    secondary: '#57606A',
    disabled: '#8C959F',
  },
  background: {
    default: '#FFFFFF',
    paper: '#F6F8FA',
  },
  divider: '#E1E4E8',
  action: {
    active: '#24292F',
    hover: '#F6F8FA',
    selected: '#F0F2F4',
    disabled: '#8C959F',
    disabledBackground: '#F6F8FA',
  },
};

const darkPalette: PaletteOptions = {
  ...clinicalBase,
  mode: 'dark',
  error: {
    main: '#FF5650',
    light: '#3D1012',
    dark: '#FF7B76',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#D29922',
    light: '#2A1E00',
    dark: '#E3B341',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#58A6FF',
    light: '#0C2D6B',
    dark: '#79C0FF',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#3FB950',
    light: '#0D2818',
    dark: '#56D364',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#E6EDF3',
    secondary: '#8B949E',
    disabled: '#6E7681',
  },
  background: {
    default: '#0D1117',
    paper: '#161B22',
  },
  divider: '#21262D',
  action: {
    active: '#E6EDF3',
    hover: '#161B22',
    selected: '#1C2128',
    disabled: '#6E7681',
    disabledBackground: '#161B22',
  },
};

function buildTheme(mode: 'light' | 'dark') {
  const palette = mode === 'light' ? lightPalette : darkPalette;

  return createTheme({
    palette,

    typography: {
      fontFamily: "'Swis721 Lt BT', 'Inter', system-ui, sans-serif",
      fontSize: 14,
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '13px',
      },
      h1: {
        fontFamily: "'Bebas Neue Pro', Arial, sans-serif",
        fontSize: '32px',
        lineHeight: 1.25,
        fontWeight: 700,
      },
      h2: { fontSize: '24px', lineHeight: 1.25, fontWeight: 600 },
      h3: { fontSize: '20px', lineHeight: 1.375, fontWeight: 600 },
      h4: { fontSize: '16px', lineHeight: 1.5, fontWeight: 600 },
      h5: { fontSize: '14px', lineHeight: 1.5, fontWeight: 600 },
      h6: { fontSize: '13px', lineHeight: 1.5, fontWeight: 600 },
      body1: { fontSize: '14px', lineHeight: 1.5 },
      body2: { fontSize: '13px', lineHeight: 1.5 },
      caption: { fontSize: '11px', lineHeight: 1.375 },
    },

    shape: { borderRadius: 4 },
    spacing: 4,

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '3px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            transition: 'all 150ms cubic-bezier(0.2, 0, 0.38, 0.9)',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: {
            borderColor: mode === 'light' ? '#D0D7DE' : '#30363D',
            '&:hover': {
              borderColor: mode === 'light' ? '#A8B1BD' : '#484F58',
              background: mode === 'light' ? '#F6F8FA' : '#161B22',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${mode === 'light' ? '#E1E4E8' : '#30363D'}`,
            borderRadius: '4px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${mode === 'light' ? '#E1E4E8' : '#30363D'}`,
          },
          elevation1: {
            boxShadow: 'none',
            border: `1px solid ${mode === 'light' ? '#E1E4E8' : '#30363D'}`,
          },
          elevation2: {
            boxShadow: 'none',
            border: `1px solid ${mode === 'light' ? '#D0D7DE' : '#484F58'}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '3px',
            fontSize: '13px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? '#E1E4E8' : '#21262D',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? '#D0D7DE' : '#30363D',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? '#0969DA' : '#58A6FF',
              borderWidth: '2px',
            },
          },
          input: { padding: '8px 12px' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: mode === 'light' ? '#E1E4E8' : '#21262D',
            padding: '8px 12px',
            fontSize: '13px',
          },
          head: {
            fontWeight: 600,
            color: mode === 'light' ? '#57606A' : '#8B949E',
            background: mode === 'light' ? '#F6F8FA' : '#161B22',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: 'none',
            color: mode === 'light' ? '#0969DA' : '#58A6FF',
            '&:hover': { textDecoration: 'underline' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '3px',
            fontSize: '11px',
            height: '24px',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: '4px', fontSize: '13px' },
          standardSuccess:
            mode === 'light'
              ? { backgroundColor: '#DFF7E8', color: '#1A7F37', border: '1px solid #2DA44E' }
              : { backgroundColor: '#0D2818', color: '#3FB950', border: '1px solid #238636' },
          standardError:
            mode === 'light'
              ? { backgroundColor: '#FFE8E7', color: '#CC453F', border: '1px solid #FF5650' }
              : { backgroundColor: '#3D1012', color: '#FF7B76', border: '1px solid #F85149' },
          standardWarning:
            mode === 'light'
              ? { backgroundColor: '#FFF8E0', color: '#955F00', border: '1px solid #BF8700' }
              : { backgroundColor: '#2A1E00', color: '#D29922', border: '1px solid #BB8009' },
          standardInfo:
            mode === 'light'
              ? { backgroundColor: '#DDF4FF', color: '#0550AE', border: '1px solid #0969DA' }
              : { backgroundColor: '#0C2D6B', color: '#58A6FF', border: '1px solid #1F6FEB' },
        },
      },
    },
  });
}

type Props = {
  children: React.ReactNode;
};

function InnerThemeProvider({ children }: Props) {
  const { mode } = useThemeMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}

const ThemeProvider = ({ children }: Props) => {
  return (
    <ThemeModeProvider>
      <InnerThemeProvider>{children}</InnerThemeProvider>
    </ThemeModeProvider>
  );
};

export default ThemeProvider;
